/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React from 'react';

import { jsx, cssMap as unboundedCssMap } from '@compiled/react';

import type { AuthProvider } from '@atlaskit/media-core/auth';

import { useProductDependencies } from '../../../controllers/use-product-dependencies';
import { CustomImageUploadRenderer } from '../custom-image-upload-renderer';
import { getImageTypeToShow } from '../utils';

export interface ImageRendererProps {
	contentId: string;
	customCollectionId?: string;
	customMediaAuthProvider?: AuthProvider;
	imageAlt?: string;
	imageSrc?: string;
	shouldShowExternalSiteImage?: boolean;
}

const unboundedStyles = unboundedCssMap({
	image: {
		height: '100%',
		width: '100%',
		objectFit: 'cover',
	},
});

export const ImageRenderer = ({
	contentId,
	imageSrc,
	imageAlt,
	shouldShowExternalSiteImage,
	customMediaAuthProvider,
	customCollectionId,
}: ImageRendererProps): JSX.Element | null => {
	const { experienceSuccessComponent } = useProductDependencies();
	if (!imageSrc) {
		return null;
	}
	const { showUnsplashImage, showCustomUploadedImage, showExternalSiteImage } = getImageTypeToShow({
		imageSrc,
	});

	const ExperienceSuccessComponent = experienceSuccessComponent || React.Fragment;

	if (showUnsplashImage || (shouldShowExternalSiteImage && showExternalSiteImage)) {
		return (
			<>
				<img data-testid="img-element" alt={imageAlt} src={imageSrc} css={unboundedStyles.image} />
				<ExperienceSuccessComponent />
			</>
		);
	} else if (showCustomUploadedImage) {
		return (
			<CustomImageUploadRenderer
				contentId={contentId}
				imageSrc={imageSrc}
				imageAltText={imageAlt}
				shouldShowErrorMessage
				mediaConfigOverride={{
					collection: customCollectionId,
					authProvider: customMediaAuthProvider,
				}}
			/>
		);
	}

	// Fallback if nothing matches
	return <ExperienceSuccessComponent />;
};
